import {
  OfferingDto,
  OfferingFilters,
  OfferingPrice,
  OfferingPriceDto
} from '@/domain/offerings/offering'
import { useToast } from '@/hooks/use-toast'
import { offeringsApi } from '@/rest/modules/offerings-api'
import { Paths } from '@/routes/paths'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { ApiResponse } from '@/domain'

export const offeringKeys = {
  all: ['offerings'] as const,
  lists: () => [...offeringKeys.all, 'list'] as const,
  list: (filters?: OfferingFilters) => [...offeringKeys.lists(), { filters }] as const,
  details: () => [...offeringKeys.all, 'detail'] as const,
  detail: (id: string) => [...offeringKeys.details(), id] as const,
  public: () => [...offeringKeys.all, 'public'] as const,
  publicDetail: (id: string) => [...offeringKeys.public(), id] as const,
}

export const useOfferings = (filters?: OfferingFilters) => {
  return useQuery({
    queryKey: offeringKeys.list(filters),
    queryFn: () => offeringsApi.getOfferings(filters),
    staleTime: 0,
    gcTime: 0,
  })
}

export const useOffering = (id: string, includePrices: boolean = false) => {
  return useQuery({
    queryKey: [...offeringKeys.detail(id), { includePrices }],
    queryFn: () => offeringsApi.getOffering(id, includePrices),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  })
}


export const usePublicOffering = (id: string) => {
  return useQuery({
    queryKey: offeringKeys.publicDetail(id),
    queryFn: () => offeringsApi.getPublicOffering(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 10,
  })
}

export const useCreateOffering = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: OfferingDto) => offeringsApi.createOffering(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offeringKeys.lists() })
      queryClient.invalidateQueries({ queryKey: offeringKeys.public() })
      toast({
        title: 'Éxito',
        description: 'Servicio creado correctamente',
        variant: 'success',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al crear el servicio',
        variant: 'destructive',
      })
    },
  })
}

export const useUpdateOffering = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OfferingDto }) =>
      offeringsApi.updateOffering(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: offeringKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: offeringKeys.lists() })
      queryClient.invalidateQueries({ queryKey: offeringKeys.public() })

      toast({
        title: 'Éxito',
        description: 'Servicio actualizado correctamente',
        variant: 'success',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al actualizar el servicio',
        variant: 'destructive',
      })
    },
  })
}

export const useDeleteOffering = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (id: string) => offeringsApi.deleteOffering(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offeringKeys.lists() })
      queryClient.invalidateQueries({ queryKey: offeringKeys.public() })
      toast({
        title: 'Éxito',
        description: 'Servicio eliminado correctamente',
        variant: 'success',
      })
      navigate({ to: `${Paths.Management.Offerings}` })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar el servicio',
        variant: 'destructive',
      })
    },
  })
}

export const useAddPrice = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ offeringId, price }: {
      offeringId: string;
      price: OfferingPriceDto
    }) => offeringsApi.addPrice(offeringId, price),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: offeringKeys.detail(variables.offeringId) })

      toast({
        title: 'Éxito',
        description: 'Precio agregado correctamente',
        variant: 'success',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al agregar el precio',
        variant: 'destructive',
      })
    },
  })
}

export const useUpdatePrice = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ offeringId, priceId, price }: {
      offeringId: string;
      priceId: string;
      price: Partial<OfferingPrice>
    }) => offeringsApi.updatePrice(offeringId, priceId, price),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: offeringKeys.detail(variables.offeringId) })

      toast({
        title: 'Éxito',
        description: 'Precio actualizado correctamente',
        variant: 'success',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al actualizar el precio',
        variant: 'destructive',
      })
    },
  })
}

export const useDeletePrice = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ offeringId, priceId }: { offeringId: string; priceId: string }) =>
      offeringsApi.deletePrice(offeringId, priceId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: offeringKeys.detail(variables.offeringId) })

      toast({
        title: 'Éxito',
        description: 'Precio eliminado correctamente',
        variant: 'success',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar el precio',
        variant: 'destructive',
      })
    },
  })
}